<!-- eslint-disable max-lines -->
<template>
  <section class="container">
    <div class="row mb-2 mb-md-5">
      <div class="col-2">
        <v-icon
          icon="chevron-left"
          class="cursor-pointer"
          @click="() => $router.back()"
        />
      </div>
      <div class="col-8">
        <div class="h3 text-center mb-1">
          <template v-if="paymentType === 'amendment'">
            Paiement de ma modification
          </template>
          <template v-else>
            Régulariser votre facture en attente
          </template>
        </div>
        <div class="text-center">
          N° {{ subscriptionReference }}
        </div>
      </div>
    </div>
    <div
      v-if="paymentType !== 'amendment'"
      class="row my-5"
    >
      <div class="col-12 col-lg-9 mx-auto">
        Pour régulariser votre facture en attente, veuillez effectuer le règlement de
        <v-highlight
          color="yellow-500"
          class="fw-bold"
        >
          {{ formattedPrice }} €
        </v-highlight>.
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-9 mx-auto">
        <div class="card border-0 shadow mb-2">
          <div
            class="card-body px-md-4 py-2 d-flex flex-column"
            :class="color"
          >
            <div
              v-if="!havePaymentMethod || userWantsToAddPaymentMethod"
              class="d-flex flex-row mx-auto bg-blue-300 fw-bold border-radius-20 mb-3"
              style="padding: 4px"
            >
              <div
                class="d-flex btn-switch"
                :class="{ 'bg-blue-600 text-white' : isCreditCard }"
                @click="isCreditCard = true"
              >
                CB
              </div>
              <div
                v-if="shouldShowIban"
                class="d-flex btn-switch ms-1"
                :class="{ 'bg-blue-600 text-white' : !isCreditCard }"
                @click="isCreditCard = false"
              >
                IBAN
              </div>
            </div>
            <div
              v-if="havePaymentMethod && !userWantsToAddPaymentMethod"
              class="row"
            >
              <div class="col-12">
                <div
                  v-for="paymentMethod in allPaymentMethods"
                  :key="paymentMethod.id"
                  class="mb-3"
                >
                  <v-display-card
                    v-if="paymentMethod.type !== 'bank_account'"
                    :payment-method="paymentMethod"
                    :price="price"
                    :stripe="stripe"
                  />
                  <div
                    v-else
                    class="card"
                  >
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <div class="me-3">
                          <i class="fas fa-university fa-2x text-primary" />
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-1">
                            Compte bancaire SEPA
                          </h6>
                          <small class="text-muted">{{ paymentMethod.iban }}</small>
                        </div>
                        <div>
                          <v-loader
                            :next="payWithBankAccount"
                            :title="`Payer ${formattedPrice} €`"
                            class="btn btn-primary"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 text-center">
                <button
                  type="button"
                  class="btn btn-block text-gray-600"
                  @click="userWantsToAddPaymentMethod = !userWantsToAddPaymentMethod"
                >
                  Ajouter un nouveau moyen de paiement
                </button>
              </div>
            </div>
            <div
              v-else
              class="row"
            >
              <div class="col-12 mb-1">
                <div class="h5 mb-1">
                  Régler par {{ isCreditCard ? 'carte bancaire' : 'prélèvement SEPA' }}
                </div>
              </div>
              <div
                v-show="isCreditCard"
                class="col-12 mb-4"
              >
                <v-credit-card
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
              <div
                v-show="!isCreditCard && !useGoCardless"
                class="col-12 mb-4"
              >
                <v-stripe-iban
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
              <div
                v-show="!isCreditCard && useGoCardless"
                class="col-12 mb-4"
              >
                <v-gocardless-iban
                  :price="price"
                  @payment:success="$router.push('/client/confirmation')"
                />
              </div>
              <div
                v-show="isCreditCard"
                class="col-12 mb-3"
              >
                <img
                  :src="`${aws}/logo-paiements.png`"
                  class="img-fluid"
                  alt="Logo mastercard"
                >
              </div>
              <div
                v-if="havePaymentMethod"
                class="col-12 text-center"
              >
                <button
                  type="button"
                  class="btn btn-block text-gray-600"
                  @click="userWantsToAddPaymentMethod = !userWantsToAddPaymentMethod"
                >
                  Utiliser une carte existante
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

import CreditCard from '@/components/checkout/stripe/CreditCard.vue';
import StripeIban from '@/components/checkout/stripe/StripeIban.vue';
import GoCardlessIban from '@/components/checkout/gocardless/GoCardlessIban.vue';
import PaymentMethodDisplay from '@/components/checkout/stripe/PaymentMethodDisplay.vue';

export default {
  name: 'CheckoutView',
  components: {
    'v-credit-card': CreditCard,
    'v-stripe-iban': StripeIban,
    'v-gocardless-iban': GoCardlessIban,
    'v-display-card': PaymentMethodDisplay,
  },
  data() {
    return {
      payment: {},
      userWantsToAddPaymentMethod: false,
      isCreditCard: true,
      useGoCardless: true,
      stripe: {},
    };
  },
  computed: {
    aws: () => `${import.meta.env.VITE_AWS_STATIC}/dashboard/png/checkout`,
    color() {
      const color = this.isCreditCard ? 'yellow' : 'green';
      return `box-shadow-${color}-300`;
    },
    ...mapGetters({
      items: 'invoices/items',
      paymentMethodsShadow: 'payments/paymentMethods',
      goCardlessBankAccounts: 'payments/goCardlessBankAccounts',
    }),
    paymentMethods() {
      return this.paymentMethodsShadow
        .filter(({ active }) => active);
    },
    allPaymentMethods() {
      const stripeMethods = this.paymentMethods;
      const bankAccountsArray = Array.isArray(this.goCardlessBankAccounts)
        ? this.goCardlessBankAccounts
        : [];

      const bankAccounts = bankAccountsArray
        .filter((account) => account.active === true)
        .map((account, index) => ({
          ...account,
          id: account.id || `bank_account_${index}`,
          type: 'bank_account',
          provider: 'gocardless',
        }));

      return [...stripeMethods, ...bankAccounts];
    },
    paymentType() {
      return (this.$route.params.type);
    },
    price() {
      return this.payment.totalToPay;
    },
    formattedPrice() {
      return parseFloat(this.price || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
    },
    subscriptionReference() {
      return (this.items.find(({ id }) => id === this.payment.invoiceItemId) || {})
        .subscriptionReference;
    },
    invoiceItem() {
      return this.items.find(({ id }) => id === this.payment.invoiceItemId) || {};
    },
    havePaymentMethod() {
      return this.allPaymentMethods.length > 0;
    },
    reference() {
      if (this.$route.query.reference) {
        return this.$route.query.reference;
      }
      return null;
    },
    shouldShowIban() {
      // Wait for payment
      if (!this.payment) {
        return false;
      }

      const isFirstPayment = this.payment?.checkout === true;
      const isNewBusiness = this.invoiceItem?.type === 'AN';

      // for others payments always show IBAN
      if (!isFirstPayment || !isNewBusiness) {
        return true;
      }
      // for first payment
       return this.price >= 500;
    },
  },
  created() {
    this.stripe = window.Stripe(import.meta.env.VITE_STRIPE_KEY);
  },
  mounted() {
    let option = 'Stripe';
    if (!this.isCreditCard) {
      option = this.useGoCardless ? 'GoCardless' : 'Stripe';
    }
    this.$store.dispatch('analytics/checkout', {
      step: 1,
      option,
    });
  },
  beforeMount() {
    this.$store.dispatch('payments/getPaymentMethods');
    this.$store.dispatch('payments/getGoCardlessBankAccounts');

    const { id } = this.$route.params;
    this.$store.dispatch('checkout/getTransactionFromId', id)
      .then((transaction) => {
        if (this.reference) {
          this.payment = transaction.payments
            .find(({ reference }) => reference === this.reference);
        } else {
          [this.payment] = transaction.payments;
        }
       // Force credit card if IBAN shouldn't be displayed
      if (!this.shouldShowIban) {
        this.isCreditCard = true;
      }
      })
      .catch(() => this.$router.replace('/failure/order_checkout'));
  },
  methods: {
    togglePaymentProvider() {
      this.useGoCardless = !this.useGoCardless;
    },
    async payWithBankAccount() {
      try {
        const { id } = this.$route.params;

        if (!id || id === 'null' || id === null || id === undefined) {
          console.error('Invalid transactionId from route:', id);
          throw new Error(`Invalid transactionId from route in Payment.vue: ${id}`);
        }

        await this.$store.dispatch('checkout/paymentGoCardless', {
          transactionId: id,
          iban: null, // IBAN not available from existing accounts
        });
        this.$router.push('/client/confirmation');
      } catch (err) {
        console.error('Payment failed:', err);
      }
    },
  },
};
</script>
<style scoped>
.btn-switch,.border-radius-20{border-radius:20px}
.btn-switch{padding:4px 8px;transition:backgroundColor 1s;background-color:var(--bs-blue-300);color:var(--bs-blue-700);cursor:pointer}
.btn-switch:hover:not(.active){background-color:var(--bs-blue-400);color:white}
.btn-switch.active{background-color:var(--bs-blue-700);color:white}
</style>
